export type HostedAuth0CallbackAttemptResult = { ok: true } | { ok: false; error: unknown };

type HostedSessionIdentity = {
  id?: string | null;
  email?: string | null;
};

type HostedSessionVerificationOptions = {
  retryDelaysMs?: readonly number[];
  wait?: (delayMs: number) => Promise<void>;
};

const DEFAULT_SESSION_RETRY_DELAYS_MS = [0, 150, 400] as const;

export type HostedAuth0CallbackAttempt = {
  hasStarted: () => boolean;
  claim: <T>(
    callback: T | null | undefined,
    exchange: (callback: T) => Promise<void>,
  ) => Promise<HostedAuth0CallbackAttemptResult> | null;
};

/**
 * Owns one destructive Auth0 callback exchange for the lifetime of a mounted
 * auth route. React may replay effects or auth bootstrap may change context
 * while the exchange is in flight; neither event may consume or restart it.
 */
export function createHostedAuth0CallbackAttempt(): HostedAuth0CallbackAttempt {
  let started = false;

  return {
    hasStarted: () => started,
    claim: <T>(callback: T | null | undefined, exchange: (callback: T) => Promise<void>) => {
      if (started || !callback) return null;
      started = true;

      return Promise.resolve()
        .then(() => exchange(callback))
        .then(
          (): HostedAuth0CallbackAttemptResult => ({ ok: true }),
          (error: unknown): HostedAuth0CallbackAttemptResult => ({ ok: false, error }),
        );
    },
  };
}

function normalizedIdentityValue(value: string | null | undefined): string {
  return String(value || "").trim().toLowerCase();
}

function hostedSessionMatches(
  exchangedUser: HostedSessionIdentity,
  sessionUser: HostedSessionIdentity,
): boolean {
  const exchangedEmail = normalizedIdentityValue(exchangedUser.email);
  const sessionEmail = normalizedIdentityValue(sessionUser.email);
  if (exchangedEmail && sessionEmail) return exchangedEmail === sessionEmail;

  const exchangedID = String(exchangedUser.id || "").trim();
  const sessionID = String(sessionUser.id || "").trim();
  return Boolean(exchangedID && sessionID && exchangedID === sessionID);
}

/**
 * A successful token exchange is not enough: the browser must be able to read
 * the resulting hosted or managed session before the app leaves the callback.
 * Short retries absorb cookie/session propagation without ever reopening Auth0.
 */
export async function verifyHostedAuthSession<T extends HostedSessionIdentity>(
  exchangedUser: T,
  readSessionUser: () => Promise<T | null>,
  options: HostedSessionVerificationOptions = {},
): Promise<T> {
  const delays = options.retryDelaysMs ?? DEFAULT_SESSION_RETRY_DELAYS_MS;
  const wait =
    options.wait ??
    ((delayMs: number) =>
      new Promise<void>((resolve) => {
        globalThis.setTimeout(resolve, delayMs);
      }));

  for (const delayMs of delays) {
    if (delayMs > 0) await wait(delayMs);
    try {
      const sessionUser = await readSessionUser();
      if (sessionUser && hostedSessionMatches(exchangedUser, sessionUser)) return sessionUser;
    } catch {
      // A bounded retry handles a transient /me failure. The caller owns the
      // terminal recovery path after all verification attempts are exhausted.
    }
  }

  throw new Error(
    "Auth0 signed you in, but GENRL could not verify the app session. Please try again.",
  );
}

export function hostedAuth0FailureMessage(code: string, storedMessage = ""): string {
  const stored = String(storedMessage || "").trim();
  if (stored) return stored;

  switch (
    String(code || "")
      .trim()
      .toLowerCase()
  ) {
    case "access_denied":
      return "Auth0 did not approve this sign-in. Please try again.";
    case "account_mismatch":
      return "Auth0 returned a different account than the one selected. Please try again and choose the intended account.";
    case "signed_out_account_reused":
      return "The previous account was selected again. You can retry and sign in with that account normally.";
    case "state_mismatch":
      return "The sign-in response expired or did not match this browser. Please start a fresh sign-in.";
    case "missing_callback":
      return "Auth0 returned without a complete sign-in response. Please start a fresh sign-in.";
    case "missing_code":
    case "callback_failed":
    case "hosted_callback_failed":
    case "session_failed":
    case "user_upsert_failed":
      return "Auth0 signed you in, but GENRL could not finish creating the app session. Please try again.";
    default:
      return "GENRL could not finish signing you in. Please try again.";
  }
}
